import { zodResolver } from '@hookform/resolvers/zod';
import { Link } from '@tanstack/react-router';
import { useForm, useWatch } from 'react-hook-form';
import { FormattedMessage } from 'react-intl';
import { z } from 'zod';

import { DateTimePicker } from '@/components/DateTimePicker';
import { NotificationBadge } from '@/components/NotificationBadge';
import { Button } from '@/components/ui/button';
import { Form } from '@/components/ui/form';
import { cn } from '@/lib/utils';

const validationSchema = z.object({
  from: z.date().optional(),
  to: z.date().optional(),
});

type FormValues = z.infer<typeof validationSchema>;

interface Props {
  hashedID: string;
  from?: string;
  to?: string;
}

export const UpdatesInterval = ({ hashedID, from, to }: Props): JSX.Element => {
  const form = useForm<FormValues>({
    resolver: zodResolver(validationSchema),
    defaultValues: {
      ...(from === undefined ? {} : { from: new Date(from) }),
      ...(to === undefined ? {} : { to: new Date(to) }),
    },
  });

  const f = useWatch({ control: form.control, name: 'from' });
  const t = useWatch({ control: form.control, name: 'to' });
  const definedValues = (f !== undefined ? 1 : 0) + (t !== undefined ? 1 : 0);

  return (
    <Form {...form}>
      <div className={cn(`border p-2 rounded-md relative`)}>
        <form className="space-y-2">
          <div className="flex flex-row justify-between items-center">
            <p>
              <FormattedMessage id="resource.updatesPeriod.form.title" />
            </p>
            <Link
              to="/resource/$resourceId"
              params={{ resourceId: hashedID }}
              onClick={() => {
                form.setValue('from', undefined);
                form.setValue('to', undefined);
              }}
              className="hover:underline mr-1"
            >
              <p>
                <FormattedMessage id="resource.updatesPeriod.form.clear" />
              </p>
            </Link>
          </div>
          <div className="flex flex-row space-x-2 items-center">
            <DateTimePicker name="from" />
            <p>and</p>
            <DateTimePicker name="to" />
            <Link
              type="submit"
              to="/resource/$resourceId"
              params={{
                resourceId: hashedID,
              }}
              search={{
                from: f?.toISOString(),
                to: t?.toISOString(),
              }}
            >
              <Button type="button" variant="outline">
                <FormattedMessage id="resource.updatesPeriod.form.submit" />
              </Button>
            </Link>
          </div>
        </form>

        {definedValues > 0 ? <NotificationBadge count={definedValues} /> : null}
      </div>
    </Form>
  );
};
